import React, { useState, useEffect } from 'react';
import { proxyImg } from '../lib/imgProxy';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useSite } from '../../../contexts/SiteContext';

const FALLBACK_LINKS = [
  { label: 'Home', href: '/' },
  {
    label: 'Books',
    items: [
      { label: 'Be The Train', href: '/book', desc: 'The foundational book' },
      { label: 'Start The Engine', href: '/books/start-the-engine', badge: 'NEW', desc: 'The creator manifesto' },
      { label: 'Tú Eres El Tren', href: '/tu-eres-el-tren', badge: 'ESPAÑOL', desc: 'Spanish edition of Be The Train' },
    ],
  },
  {
    label: 'Media',
    items: [
      { label: 'Podcast', href: '/podcast', desc: 'The Be The Train podcast' },
      { label: 'Case Studies', href: '/case-studies/you-me-and-her', desc: 'You, Me & Her' },
    ],
  },
  {
    label: 'Courses',
    items: [
      { label: 'Creator Lab', href: '/creator-lab', desc: 'The project accelerator' },
      { label: 'Feature Filmmaker Intensive', href: '/upcoming-courses', desc: 'Three-week intensive' },
    ],
  },
  { label: 'Community', href: '/community' },
];

const FALLBACK_CTA = { label: 'Schedule a Call', href: '/book-a-call' };

const Badge = ({ badge }) =>
  badge === 'NEW' ? (
    <span className="ml-2 px-1.5 py-0.5 bg-[#00A86B] text-black text-[9px] font-bold uppercase tracking-wider leading-none align-middle">
      NEW
    </span>
  ) : (
    <span className="ml-2 px-1.5 py-0.5 border border-white/30 text-white/60 text-[9px] font-bold uppercase tracking-wider leading-none align-middle">
      {badge}
    </span>
  );

const BTTNavigation = () => {
  const { navConfig, siteConfig } = useSite();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Use DB config only if it has the new dropdown structure
  const cfgLinks = navConfig?.links;
  const navLinks =
    Array.isArray(cfgLinks) && cfgLinks.some((l) => Array.isArray(l.items)) ? cfgLinks : FALLBACK_LINKS;
  const cta = navConfig?.cta?.href === '/book-a-call' ? navConfig.cta : FALLBACK_CTA;
  const logoUrl =
    siteConfig?.theme?.logo ||
    'https://customer-assets.emergentagent.com/job_5fff2c07-dfff-4220-a200-8d4fd9f6d613/artifacts/i6xi2h68_BTTLogo.png';

  const isActive = (href) => {
    if (!href || href.startsWith('http')) return false;
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };
  const isGroupActive = (group) => (group.items || []).some((i) => isActive(i.href));

  const slug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
          scrolled || menuOpen || openDropdown
            ? 'bg-black/80 backdrop-blur-xl border-b border-white/[0.06]'
            : 'bg-transparent border-b border-transparent'
        }`}
        data-testid="main-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="h-20 flex items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 z-10 flex-shrink-0" data-testid="nav-logo">
              <img src={proxyImg(logoUrl, { w: 220 })} alt="Be The Train" loading="eager" decoding="async" className="h-10 sm:h-12" />
            </Link>

            {/* Desktop links */}
            <div className="hidden lg:flex items-center gap-1" data-testid="desktop-nav-links">
              {navLinks.map((link) =>
                link.items ? (
                  <div
                    key={link.label}
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(link.label)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button
                      onClick={() => setOpenDropdown(openDropdown === link.label ? null : link.label)}
                      className={`flex items-center gap-1 px-4 py-3 text-[13px] uppercase tracking-[0.18em] transition-colors ${
                        isGroupActive(link) || openDropdown === link.label
                          ? 'text-[#00A86B]'
                          : 'text-white/70 hover:text-white'
                      }`}
                      data-testid={`nav-dropdown-${slug(link.label)}`}
                      aria-expanded={openDropdown === link.label}
                    >
                      {link.label}
                      <ChevronDown
                        size={13}
                        className={`transition-transform duration-200 ${openDropdown === link.label ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {/* Dropdown panel */}
                    <div
                      className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 w-80 transition-all duration-200 ${
                        openDropdown === link.label
                          ? 'opacity-100 translate-y-0 pointer-events-auto'
                          : 'opacity-0 -translate-y-1 pointer-events-none'
                      }`}
                      data-testid={`nav-dropdown-panel-${slug(link.label)}`}
                    >
                      <div className="bg-black border border-white/10 shadow-2xl shadow-black/60 py-2">
                        {link.items.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            className="block px-5 py-3.5 hover:bg-white/[0.04] transition-colors group"
                            data-testid={`nav-item-${slug(item.label)}`}
                          >
                            <span
                              className={`text-sm font-semibold tracking-wide ${
                                isActive(item.href) ? 'text-[#00A86B]' : 'text-white group-hover:text-[#00A86B]'
                              } transition-colors`}
                            >
                              {item.label}
                              {item.badge && <Badge badge={item.badge} />}
                            </span>
                            {item.desc && (
                              <span className="block text-xs text-white/40 mt-0.5">{item.desc}</span>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`px-4 py-3 text-[13px] uppercase tracking-[0.18em] transition-colors ${
                      isActive(link.href) ? 'text-[#00A86B]' : 'text-white/70 hover:text-white'
                    }`}
                    data-testid={`nav-link-${slug(link.label)}`}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </div>

            {/* Right: CTA + mobile toggle */}
            <div className="flex items-center gap-3 z-10 flex-shrink-0">
              <Link
                to={cta.href}
                className="px-4 py-3 sm:px-5 sm:py-2.5 bg-[#00A86B] text-white font-bold text-[11px] sm:text-xs uppercase tracking-wider hover:bg-[#00A86B]/90 transition-colors whitespace-nowrap min-h-[44px] flex items-center"
                data-testid="nav-cta-schedule-call"
              >
                {cta.label}
              </Link>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden p-3 -mr-2 text-white/70 hover:text-white transition-colors min-h-[44px] min-w-[44px]"
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {menuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
        <div
          className={`absolute top-20 left-0 right-0 bottom-0 bg-black overflow-y-auto transition-transform duration-300 ease-out ${
            menuOpen ? 'translate-y-0' : '-translate-y-4'
          }`}
          data-testid="mobile-nav-panel"
        >
          <div className="px-6 py-8 pb-28">
            {navLinks.map((link) =>
              link.items ? (
                <div key={link.label} className="mb-6">
                  <p className="text-[#00A86B] text-[11px] uppercase tracking-[0.25em] font-semibold mb-1 px-1">
                    {link.label}
                  </p>
                  {link.items.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setMenuOpen(false)}
                      className={`block py-3.5 px-1 border-b border-white/[0.05] ${
                        isActive(item.href) ? 'text-[#00A86B]' : 'text-white'
                      }`}
                      data-testid={`mobile-nav-item-${slug(item.label)}`}
                    >
                      <span className="text-base font-semibold tracking-wide">
                        {item.label}
                        {item.badge && <Badge badge={item.badge} />}
                      </span>
                      {item.desc && <span className="block text-xs text-white/40 mt-0.5">{item.desc}</span>}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMenuOpen(false)}
                  className={`block py-4 px-1 mb-2 text-lg font-semibold uppercase tracking-[0.15em] border-b border-white/[0.05] ${
                    isActive(link.href) ? 'text-[#00A86B]' : 'text-white'
                  }`}
                  data-testid={`mobile-nav-link-${slug(link.label)}`}
                >
                  {link.label}
                </Link>
              )
            )}

            <Link
              to={cta.href}
              onClick={() => setMenuOpen(false)}
              className="block w-full mt-6 px-6 py-4 bg-[#00A86B] text-white text-center font-bold uppercase tracking-wider hover:bg-[#00A86B]/90 transition-colors"
              data-testid="mobile-nav-cta"
            >
              {cta.label}
            </Link>
          </div>
        </div>
      </div>

      {/* Spacer for fixed nav */}
      <div className="h-20" />
    </>
  );
};

export default BTTNavigation;
